import React, { useState, useRef, useEffect } from 'react';
import { UploadCloud, FileText, X, CheckCircle2, FolderUp } from 'lucide-react';

interface FileDropZoneProps {
  label: string;
  subLabel: string;
  accept: string;
  multiple?: boolean;
  /** When true (and multiple), shows an "Importer un dossier" button that picks an entire folder. */
  allowDirectory?: boolean;
  files: File[];
  onFilesChange: (files: File[]) => void;
  icon?: React.ReactNode;
}

/** Parse an `accept` string ("..pdf,.doc") into a list of lowercase extensions. */
const parseAcceptExtensions = (accept: string): string[] =>
  accept
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.startsWith('.'));

/** Keep only files whose name matches one of the accepted extensions. No-op if accept uses MIME types. */
const filterByAccept = (files: File[], accept: string): File[] => {
  const exts = parseAcceptExtensions(accept);
  if (exts.length === 0) return files;
  return files.filter((f) => exts.some((ext) => f.name.toLowerCase().endsWith(ext)));
};

/** Merge new files into existing, de-duplicating by name + size. */
const mergeFiles = (existing: File[], incoming: File[]): File[] => {
  const seen = new Set(existing.map((f) => `${f.name}::${f.size}`));
  const merged = [...existing];
  for (const f of incoming) {
    const key = `${f.name}::${f.size}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(f);
    }
  }
  return merged;
};

const FileDropZone: React.FC<FileDropZoneProps> = ({
  label,
  subLabel,
  accept,
  multiple = false,
  allowDirectory = false,
  files,
  onFilesChange,
  icon
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dirInputRef = useRef<HTMLInputElement>(null);

  // `webkitdirectory` is non-standard and has no React prop — set it on the DOM node directly.
  useEffect(() => {
    if (dirInputRef.current) {
      dirInputRef.current.setAttribute('webkitdirectory', '');
      dirInputRef.current.setAttribute('directory', '');
    }
  }, []);

  const addFiles = (incoming: File[]) => {
    if (incoming.length === 0) return;
    if (multiple) {
      onFilesChange(mergeFiles(files, filterByAccept(incoming, accept)));
    } else {
      onFilesChange([incoming[0]]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(Array.from(e.target.files));
    }
    e.target.value = '';
  };

  const handleDirSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // A folder pick returns the whole tree; filterByAccept (inside addFiles) keeps only CVs.
      addFiles(Array.from(e.target.files));
    }
    e.target.value = '';
  };

  const removeFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    onFilesChange(newFiles);
  };

  return (
    <div className="w-full">
      {label && <h3 className="text-sm font-semibold text-slate-700 mb-2">{label}</h3>}

      {/* Hidden inputs kept OUTSIDE the clickable zone so programmatic .click() doesn't bubble back into it */}
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleFileSelect}
      />
      <input
        ref={dirInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleDirSelect}
      />

      {/* Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 transition-all cursor-pointer flex flex-col items-center justify-center text-center group
          ${isDragging
            ? 'border-amber-500 bg-amber-50/50'
            : 'border-slate-300 hover:border-amber-400 hover:bg-slate-50'
          }
          ${files.length > 0 && !multiple ? 'bg-emerald-50/30 border-emerald-200' : ''}
        `}
      >
        {files.length > 0 && !multiple ? (
             <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300">
                <div className="w-12 h-12 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-3">
                    <CheckCircle2 size={24} />
                </div>
                <p className="text-sm font-medium text-emerald-800">{files[0].name}</p>
                <p className="text-xs text-emerald-600 mt-1">Prêt pour analyse</p>
             </div>
        ) : (
            <>
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-colors ${isDragging ? 'bg-amber-100 text-amber-600' : 'bg-slate-100 text-slate-400 group-hover:text-amber-500 group-hover:bg-amber-50'}`}>
                {icon || <UploadCloud size={24} />}
                </div>
                <p className="text-sm font-medium text-slate-700">
                <span className="text-amber-600 hover:underline">Cliquez pour uploader</span> ou glissez-déposez
                </p>
                <p className="text-xs text-slate-500 mt-1">{subLabel}</p>
            </>
        )}
      </div>

      {/* Folder import — outside the clickable drop zone to avoid triggering the file dialog */}
      {allowDirectory && multiple && (
        <button
          type="button"
          onClick={() => dirInputRef.current?.click()}
          className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:border-amber-400 hover:text-amber-600 transition-colors"
        >
          <FolderUp size={16} />
          Importer un dossier entier
        </button>
      )}

      {/* File List for Multiple */}
      {multiple && files.length > 0 && (
        <div className="mt-4 space-y-2 max-h-64 overflow-y-auto pr-1">
          <div className="flex items-center justify-between px-1">
            <span className="text-xs font-medium text-slate-500">{files.length} fichier{files.length > 1 ? 's' : ''} prêt{files.length > 1 ? 's' : ''}</span>
            <button
              type="button"
              onClick={() => onFilesChange([])}
              className="text-xs text-slate-400 hover:text-red-500 transition-colors"
            >
              Tout retirer
            </button>
          </div>
          {files.map((file, idx) => (
            <div key={`${file.name}-${file.size}-${idx}`} className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg shadow-sm">
              <div className="flex items-center gap-3 overflow-hidden">
                <div className="p-2 bg-slate-100 rounded-md text-slate-500">
                    <FileText size={16} />
                </div>
                <div className="flex flex-col min-w-0">
                    <span className="text-sm font-medium text-slate-700 truncate">{file.name}</span>
                    <span className="text-xs text-slate-400">{(file.size / 1024).toFixed(0)} KB</span>
                </div>
              </div>
              <button
                onClick={() => removeFile(idx)}
                className="text-slate-400 hover:text-red-500 transition-colors p-1"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileDropZone;
